import type { ColumnDef } from '@tanstack/react-table';

import Badge from 'components/partials/badge/badge';
import IconButton from 'components/partials/button/icon-button';

import AwardAmountCell from './components/award-amount-cell';
import AwardLogicCell from './components/award-logic-cell';
import {
  getCeebRowWarning,
  getSpecialAwardChips,
} from './helpers/special-award-row';
import type { SpecialAwardChipContext } from './helpers/special-award-row';

import Icons from 'assets/icons';

import type { SpecialAwardRow } from './calculations-step.types';

const commonHeaderStyles =
  'h-11 border-r-2 border-white font-semibold cursor-default text-xs py-0 align-middle';

interface AwardLogicOption {
  value: string;
  label: string;
}

export interface CalculationsTableSettingsParams {
  chipContext: SpecialAwardChipContext;
  awardLogicOptions: AwardLogicOption[];
  onEdit: (rule: SpecialAwardRow) => void;
  onDelete: (rule: SpecialAwardRow) => void;
  onAmountCommit: (rule: SpecialAwardRow, amount: number | null) => void;
  onLogicChange: (rule: SpecialAwardRow, logic: string) => void;
}

export const getCalculationsTableSettings = ({
  chipContext,
  awardLogicOptions,
  onEdit,
  onDelete,
  onAmountCommit,
  onLogicChange,
}: CalculationsTableSettingsParams): ColumnDef<SpecialAwardRow>[] => [
  {
    id: 'details',
    header: () => <span>DETAILS</span>,
    cell: ({ row }) => {
      const rule = row.original;
      const chips = getSpecialAwardChips(rule, chipContext);
      const ceebWarning = getCeebRowWarning(
        rule,
        chipContext.ceebMatchSummaries
      );

      return (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
          {!!rule.description && (
            <p className="min-w-0 whitespace-pre-line break-words text-sm text-gray-700">
              {rule.description}
            </p>
          )}
          <div className="flex min-w-0 flex-wrap items-center gap-1.5">
            {chips.map((chip, index) => (
              <Badge
                key={`${chip}-${index}`}
                text={chip}
                variant="neutral-outlined"
              />
            ))}
            {!chips.length && <span className="text-sm text-gray-400">—</span>}
            {!!ceebWarning && (
              <span
                className="flex items-center gap-1 text-sm text-amber-700"
                data-testid={`special-award-ceeb-warning-${rule.id}`}
              >
                <Icons.Warning customClass="size-4" />
                {ceebWarning}
              </span>
            )}
            <IconButton
              variant="reserve-text"
              size="xs"
              icon={<Icons.Edit />}
              onClick={() => onEdit(rule)}
              aria-label="Edit special award"
              className="text-navy-500 hover:!bg-navy-50 hover:!text-navy-700"
              data-testid={`edit-special-award-${rule.id}`}
            />
          </div>
        </div>
      );
    },
    headerClassName: `${commonHeaderStyles} rounded-tl-md rounded-bl-md`,
    className: 'text-left px-2.5 py-2 w-[50%] max-w-0 align-top',
    enableSorting: false,
  },
  {
    id: 'awardAmount',
    header: () => <span>AWARDING AMOUNT</span>,
    cell: ({ row }) => {
      const rule = row.original;

      return (
        <div className="px-0.5 pt-[5px]">
          <AwardAmountCell
            value={rule.awardAmount}
            onCommit={(amount) => onAmountCommit(rule, amount)}
            data-testid={`award-amount-${rule.id}`}
          />
        </div>
      );
    },
    headerClassName: commonHeaderStyles,
    className: 'w-[18%] px-2.5 align-top',
    enableSorting: false,
  },
  {
    id: 'awardLogic',
    header: () => <span>AWARDING LOGIC</span>,
    cell: ({ row }) => {
      const rule = row.original;

      return (
        <div className="px-0.5 pt-[5px]">
          <AwardLogicCell
            value={rule.awardLogic}
            options={awardLogicOptions}
            onChange={(logic) => onLogicChange(rule, logic)}
            data-testid={`award-logic-select-${rule.id}`}
          />
        </div>
      );
    },
    headerClassName: commonHeaderStyles,
    className: 'w-[26%] px-2.5 align-top',
    enableSorting: false,
  },
  {
    id: 'actions',
    header: () => null,
    cell: ({ row }) => {
      const rule = row.original;

      return (
        <IconButton
          variant="reserve-text"
          size="xs"
          icon={<Icons.Delete />}
          onClick={() => onDelete(rule)}
          aria-label="Remove special award"
          className="mx-auto text-navy-500 hover:!bg-navy-50 hover:!text-navy-700"
          data-testid={`remove-special-award-${rule.id}`}
        />
      );
    },
    headerClassName: 'rounded-tr-md rounded-br-md text-xs h-8 py-0',
    className: 'w-[6%] px-2 align-top pt-[7px] text-center',
    enableSorting: false,
  },
];
